import { Link } from 'react-router';
import { Icon } from './ui/Icon.js';

type EmptyStateVariant = 'default' | 'compact' | 'success' | 'onboarding' | 'no-results';

interface EmptyStateProps {
  /** Boxicon name (e.g. 'bx-search'), not a component. Spec 230. */
  icon?: string;
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
  /**
   * Spec 230 — Kridt has one empty-state recipe (a `.hub-plate--flat` well,
   * one line, one action), not five colour schemes, so the well now renders
   * identically regardless of `variant`. Kept in the prop type purely so the
   * ~15 existing call sites that pass it keep compiling unchanged.
   */
  variant?: EmptyStateVariant;
  testId?: string;
  /** Ghost when the page head already carries the view's one ink primary. */
  actionVariant?: 'primary' | 'ghost';
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  actionHref,
  testId,
  actionVariant = 'primary',
}: EmptyStateProps) {
  // Spec 250 (review P1-7): the one empty state — a sunken well, one line, one
  // action. The action is a router Link: a plain <a href> reloaded the whole
  // SPA to move between two of its own pages.
  return (
    <div className="hub-plate hub-plate--flat flex flex-col items-center gap-2 px-6 py-12 text-center" data-testid={testId}>
      {icon && <Icon name={icon} className="text-2xl text-[var(--hub-color-ink-subtle)]" />}
      <p className="text-base font-semibold text-[var(--hub-color-ink)]">{title}</p>
      {description && <p className="max-w-sm text-sm text-[var(--hub-color-ink-muted)]">{description}</p>}
      {actionLabel && actionHref && (
        <Link to={actionHref} className={`hub-btn hub-btn--${actionVariant} mt-2`}>
          {actionLabel}
        </Link>
      )}
    </div>
  );
}
